import { useState, useCallback } from 'react';
import { GoogleGenAI } from '@google/genai';
import { uploadGeneratedImage } from './uploadGeneratedImage';
import makeCloudinaryUrlForPhoto from '../../afa-photo';

// Initialize the Gemini API client
// Note: In production, store this key securely (environment variable or backend)
const API_KEY = process.env.REACT_APP_GEMINI_API_KEY || '';

const ai = new GoogleGenAI({ apiKey: API_KEY });

/**
 * Generate a unique chat ID
 */
function generateChatId() {
  return `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Convert image URL to base64
 */
async function imageUrlToBase64(url) {
  const response = await fetch(url);
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      // Remove data URL prefix to get just the base64
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Custom hook for AI image chat functionality
 * Manages chat state, Gemini API interaction, and persistence
 */
function useAiImageChat(product, onChatUpdate) {
  const [currentChat, setCurrentChat] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Get product data
  const photo = product?.get ? product.get('photo') : product?.photo;
  const shopId = product?.get ? product.get('shopId') : product?.shopId;
  
  /**
   * Load existing chat history from product
   */
  const loadChatHistory = useCallback(() => {
    const aiImageChats = product?.get ? product.get('aiImageChats') : product?.aiImageChats;
    if (aiImageChats && Array.isArray(aiImageChats)) {
      setChatHistory(aiImageChats);
    } else {
      setChatHistory([]);
    }
  }, [product]);
  
  /**
   * Start a new chat session
   */
  const startNewChat = useCallback(() => {
    const newChat = {
      id: generateChatId(),
      createdAt: new Date(),
      originalPhoto: photo || null,
      usedReference: true, // Will be set on first message
      messages: [],
    };
    
    setCurrentChat(newChat);
    setError(null);
  }, [photo]);
  
  /**
   * Select an existing chat from history
   */
  const selectChat = useCallback((chatId) => {
    const chat = chatHistory.find(c => c.id === chatId);
    if (chat) {
      setCurrentChat(chat);
    }
  }, [chatHistory]);
  
  /**
   * Send a message to Gemini and get a response
   * Uses the new @google/genai SDK with streaming and imageConfig
   */
  const sendMessage = useCallback(async (promptText, useReference) => {
    if (!API_KEY) {
      setError('Gemini API key not configured. Set REACT_APP_GEMINI_API_KEY environment variable.');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Create user message
      const userMessage = {
        role: 'user',
        text: promptText,
        timestamp: new Date(),
      };
      
      // Update current chat with user message
      const isFirstMessage = !currentChat?.messages?.length;
      let updatedChat = currentChat || {
        id: generateChatId(),
        createdAt: new Date(),
        originalPhoto: photo || null,
        usedReference: useReference,
        messages: [],
      };
      
      // Set usedReference on first message
      if (isFirstMessage) {
        updatedChat = { ...updatedChat, usedReference: useReference };
      }
      
      updatedChat = {
        ...updatedChat,
        messages: [...(updatedChat.messages || []), userMessage],
      };
      
      setCurrentChat(updatedChat);
      
      // Find the last generated image from previous messages (for multi-turn editing)
      const lastGeneratedImage = [...(updatedChat.messages || [])]
        .reverse()
        .find(msg => msg.role === 'model' && msg.imageBase64);
      
      // Build the request contents with parts
      const parts = [];
      
      // For follow-up messages, include the last generated image so Gemini knows what to edit
      if (!isFirstMessage && lastGeneratedImage?.imageBase64) {
        const base64Data = lastGeneratedImage.imageBase64.replace(/^data:[^;]+;base64,/, '');
        parts.push({
          inlineData: {
            mimeType: 'image/png',
            data: base64Data,
          },
        });
      } else if (isFirstMessage && useReference && photo) {
        // First message: include the original product image
        try {
          const imageUrl = makeCloudinaryUrlForPhoto(photo, { width: 1024, height: 1024 });
          if (imageUrl && imageUrl !== 'no-pic') {
            const base64 = await imageUrlToBase64(imageUrl);
            parts.push({
              inlineData: {
                mimeType: 'image/jpeg',
                data: base64,
              },
            });
          }
        } catch (imgError) {
          console.warn('Failed to load reference image:', imgError);
        }
      }
      
      // Add the text prompt
      parts.push({ text: promptText });
      
      // For multi-turn image editing, we use a simplified approach:
      // Just send the last generated image with the new prompt (following Google's example)
      // This avoids complex thought signature handling in conversation history
      const contents = [
        {
          role: 'user',
          parts,
        },
      ];
      
      // Configure the model with image generation settings
      // Using imageConfig to control output size/aspect ratio
      const config = {
        responseModalities: ['IMAGE', 'TEXT'],
        imageConfig: {
          imageSize: '1K', // Maintains better aspect ratio matching
        },
      };
      
      // Use streaming for better response handling (same as AI Studio)
      const response = await ai.models.generateContentStream({
        model: 'gemini-3-pro-image-preview', // Use 'gemini-3-pro-image-preview' if available
        config,
        contents,
      });
      
      // Extract text and image from streaming response
      let responseText = '';
      let imageBase64 = null;
      let imageMimeType = 'image/png';
      
      for await (const chunk of response) {
        if (!chunk.candidates || !chunk.candidates[0]?.content?.parts) {
          continue;
        }
        
        for (const part of chunk.candidates[0].content.parts) {
          if (part.text) {
            responseText += part.text;
          }
          if (part.inlineData) {
            imageBase64 = part.inlineData.data;
            imageMimeType = part.inlineData.mimeType || 'image/png';
          }
        }
      }
      
      // Create AI response message
      const aiMessage = {
        role: 'model',
        text: responseText || 'Here is the generated image.',
        timestamp: new Date(),
      };
      
      // If we got an image, upload it to Firebase Storage
      if (imageBase64) {
        try {
          const { downloadUrl, storagePath } = await uploadGeneratedImage(
            imageBase64,
            imageMimeType,
            shopId,
            updatedChat.id,
            updatedChat.messages.length // Use message index
          );
          
          aiMessage.imageUrl = downloadUrl;
          aiMessage.storagePath = storagePath;
          aiMessage.imageBase64 = `data:${imageMimeType};base64,${imageBase64}`; // Keep for immediate display
        } catch (uploadError) {
          console.error('Failed to upload image:', uploadError);
          // Still show the image using base64
          aiMessage.imageUrl = `data:${imageMimeType};base64,${imageBase64}`;
          aiMessage.uploadError = uploadError.message;
        }
      }
      
      // Update chat with AI response
      const finalChat = {
        ...updatedChat,
        messages: [...updatedChat.messages, aiMessage],
      };
      
      setCurrentChat(finalChat);
      
      // Update chat history
      setChatHistory(prev => {
        const existingIndex = prev.findIndex(c => c.id === finalChat.id);
        if (existingIndex >= 0) {
          const updated = [...prev];
          updated[existingIndex] = finalChat;
          return updated;
        }
        return [...prev, finalChat];
      });
      
      // Notify parent to persist
      if (onChatUpdate) {
        onChatUpdate(finalChat, chatHistory);
      }
      
    } catch (err) {
      console.error('Error sending message to Gemini:', err);
      setError(err.message || 'Failed to generate image');
    } finally {
      setIsLoading(false);
    }
  }, [currentChat, photo, shopId, chatHistory, onChatUpdate]);
  
  return {
    currentChat,
    chatHistory,
    isLoading,
    error,
    loadChatHistory,
    startNewChat,
    selectChat,
    sendMessage,
    setChatHistory, // Expose for external updates (e.g., real-time listener)
  };
}

export default useAiImageChat;
